import React, {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  asyncModals,
  type AsyncModalsTypes,
} from "@/features/ui/util/async-modal-components";
import { captureMessage } from "@/monitoring";
import { cn } from "@/utils/cn";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/scroll-lock";

// Dynamically map modal configurations to their respective components and props
const modals = Object.entries(asyncModals).reduce(
  (acc, [key, component]) => {
    acc[key as keyof AsyncModalsTypes] = {
      component,
      LazyModal: React.lazy(() =>
        component().then((mod) => ({ default: mod.default })),
      ),
      props: {} as AsyncModalsTypes[keyof AsyncModalsTypes],
    };
    return acc;
  },
  {} as {
    [K in keyof AsyncModalsTypes]: {
      component: () => Promise<{ default: React.FC<any> }>;
      LazyModal: React.LazyExoticComponent<React.FC<any>>;
      props?: AsyncModalsTypes[K];
    };
  },
);

type PropsOf<T extends keyof ModalsConfig> = Expand<ModalsConfig[T]["props"]>;

// Expand complex or inferred types into their fully resolved & readable structure
type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;
type ModalsConfig = typeof modals;
type OpenModalFunction = <T extends keyof ModalsConfig>(
  key: T,
  ...args: {} extends PropsOf<T> ? [props?: PropsOf<T>] : [props: PropsOf<T>]
) => void;

type IModalContext = {
  closeModal(): void;
  replaceModal: OpenModalFunction;
  openModal: OpenModalFunction;
  isModalOpen: boolean;
};

const ModalContext = createContext<IModalContext | null>(null);

interface ModalProviderProps {
  children: React.ReactNode;
}

const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modalStack, setModalStack] = useState<
    {
      key: keyof ModalsConfig;
      props?: ModalsConfig[keyof ModalsConfig]["props"];
      id: string;
    }[]
  >([]);
  const location = useLocation();

  const isModalOpen = modalStack.length > 0;

  const currentLocation = useMemo(() => {
    return location.pathname;
  }, [isModalOpen]);

  const _openModal = (key: keyof ModalsConfig, props?: any) => {
    if (!modals[key]) throw new Error(`Modal ${key} not defined`);

    setModalStack((prevStack) => {
      if (prevStack.some((modal) => modal.key === key)) {
        captureMessage(`Modal ${key} is already open`);
        return prevStack;
      }

      return [...prevStack, { key, props, id: uuidv4() }];
    });
  };

  const _replaceModal = (key: keyof ModalsConfig, props?: any) => {
    if (!modals[key]) throw new Error(`Modal ${key} not defined`);

    setModalStack((prevStack) => {
      if (prevStack.length === 0) {
        return [{ key, props, id: uuidv4() }];
      }

      const newStack = [...prevStack];
      newStack[newStack.length - 1] = { key, props, id: uuidv4() };
      return newStack;
    });
  };

  const openModal = _openModal as OpenModalFunction;
  const replaceModal = _replaceModal as OpenModalFunction;

  const closeModal = () => {
    setModalStack((prevStack) => prevStack.slice(0, -1));
  };

  const value = useMemo(
    () => ({
      closeModal,
      isModalOpen,
      openModal,
      replaceModal,
    }),
    [isModalOpen],
  );

  useEffect(() => {
    if (currentLocation !== location.pathname && isModalOpen) {
      setModalStack([]);
    }
  }, [location.pathname, isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  }, [isModalOpen]);

  return (
    <ModalContext.Provider value={value}>
      {isModalOpen && (
        <>
          <div
            role="presentation"
            id="modal-overlay"
            className="pointer-events-none fixed inset-0 z-1001 size-full bg-gray-500/90 backdrop-blur-xs dark:bg-gray-600/90"
            onClick={() => {
              const topModal = modalStack[modalStack.length - 1];
              if (topModal?.props?.disableClose) {
                return;
              }

              closeModal();
            }}
          />

          {modalStack.map(({ key, props, id }, index) => {
            const { LazyModal } = modals[key];
            return (
              <div
                key={id}
                className={cn({
                  invisible: index !== modalStack.length - 1,
                })}
                data-testid="modal"
              >
                <Suspense fallback={null}>
                  <LazyModal
                    {...props}
                    onClose={() => {
                      if (props?.onClose) {
                        props.onClose();
                      } else {
                        closeModal();
                      }
                    }}
                  />
                </Suspense>
              </div>
            );
          })}
        </>
      )}

      {children}
    </ModalContext.Provider>
  );
};

const useModalContext = (): IModalContext => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};

export { ModalProvider, useModalContext, type IModalContext };
